import React, { useCallback, useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { ChevronDown, ChevronRight, Search, Users } from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';
import NewtonsCradleLoader from '../components/common/NewtonsCradleLoader';
import { getThemeColor } from '../config/appConfig';
import apiService, {
  extractClientsList,
  extractClientsListPagination,
} from '../utils/apiService';
import { useFetchOnceByKey } from '../hooks/useMountOnce';

const THEME = getThemeColor('primary');
const KYC_TABS = ['All', 'Pending', 'In Review', 'Verified', 'Approved', 'Rejected'];
const PAGE_SIZE_OPTIONS = [
  { value: '10', label: '10' },
  { value: '50', label: '50' },
  { value: '100', label: '100' },
];
const CLIENTS_EXPORT_PAGE_SIZE = 10000;

/** Codes are stored without this display prefix (same as the Commission screens). */
const CLIENT_CODE_PREFIX = 'A-UC-';

const formatClientCode = (code) => {
  const value = String(code ?? '').trim();
  if (!value || value === '—') return '—';
  return value.toUpperCase().startsWith(CLIENT_CODE_PREFIX) ? value : `${CLIENT_CODE_PREFIX}${value}`;
};

const formatDate = (value) =>
  value
    ? new Date(value).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
    : '—';

const normalizeTradingAccount = (account) => {
  if (account == null) return null;
  if (typeof account === 'string' || typeof account === 'number') {
    return { loginId: String(account), status: 'Active', isArchived: false };
  }
  const isArchived = account.isArchived === true || String(account.status ?? '').toLowerCase() === 'archived';
  return {
    loginId: String(
      account.loginId ??
      account.accountNo ??
      account.accountNumber ??
      account.mtAccount ??
      account.id ??
      '—'
    ),
    isArchived,
    status: isArchived ? 'Archived' : (account.status || 'Active'),
  };
};

const toAmount = (value) => {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
};

const formatMoney = (value) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2 }).format(toAmount(value));

const formatLots = (value) =>
  new Intl.NumberFormat('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 2 }).format(toAmount(value));

const normalizeIsDeposit = (value) => {
  if (value === true || value === 1) return true;
  if (value === false || value === 0 || value == null || value === '') return false;
  const s = value.toString().trim().toLowerCase();
  if (s === 'yes' || s === 'true' || s === '1') return true;
  if (s === 'no' || s === 'false' || s === '0') return false;
  return false;
};

const mapClient = (item, index = 0) => {
  const rawAccounts = Array.isArray(item.userTradingAccounts) ? item.userTradingAccounts : [];
  const activeAccounts = rawAccounts.map(normalizeTradingAccount).filter(Boolean);
  // tradingAccountDetails also carries archived accounts and each account's status (falls back to the plain list).
  const rawDetails = Array.isArray(item.tradingAccountDetails ?? item.TradingAccountDetails)
    ? (item.tradingAccountDetails ?? item.TradingAccountDetails)
    : null;
  const tradingAccounts = rawDetails ? rawDetails.map(normalizeTradingAccount).filter(Boolean) : activeAccounts;
  const kycStatus = (item.userKYCStatus ?? item.kycStatus ?? '').toString().trim() || '—';
  const totalCommission = Number(item.totalCommission ?? item.TotalCommission ?? 0);

  return {
    id: item.userCode ?? item.id ?? item.clientId ?? index,
    userCode: item.userCode ?? '—',
    fullName: (item.fullName ?? item.FullName ?? '').toString().trim(),
    tradingAccountsCount: activeAccounts.length,
    tradingAccounts,
    registrationDate: item.userRegistrationDate ?? item.registrationDate ?? null,
    kycStatus,
    isDeposit: normalizeIsDeposit(item.isDeposit ?? item.IsDeposit),
    firstDeposit: Number.isFinite(Number(item.firstDeposit ?? item.FirstDeposit)) ? Number(item.firstDeposit ?? item.FirstDeposit ?? 0) : 0,
    totalCommission: Number.isFinite(totalCommission) ? totalCommission : 0,
    totalDeposit: toAmount(item.totalDeposit ?? item.TotalDeposit),
    totalWithdrawal: toAmount(item.totalWithdrawal ?? item.TotalWithdrawal),
    totalLots: toAmount(item.totalLots ?? item.TotalLots),
  };
};

const fetchAllClientsForExport = async (searchTerm, loginId) => {
  const size = CLIENTS_EXPORT_PAGE_SIZE;
  const allRows = [];
  let page = 1;
  let totalPages = 1;

  do {
    const response = await apiService.getClientsList({
      pageNumber: page,
      pageSize: size,
      searchTerm: searchTerm ?? '',
      loginId: loginId ?? '',
      type: '',
      sortBy: '',
      sortOrder: '',
    });
    const list = extractClientsList(response).map((item, index) =>
      mapClient(item, allRows.length + index)
    );
    const meta = extractClientsListPagination(response, size);
    allRows.push(...list);
    totalPages = meta.totalPages || 1;
    if (list.length === 0) break;
    page += 1;
  } while (page <= totalPages);

  return allRows;
};

const KycBadge = ({ status }) => {
  const key = (status || '').toLowerCase();
  let styles = 'bg-gray-100 text-gray-600';
  if (key === 'approved' || key === 'verified') styles = 'bg-emerald-50 text-emerald-700';
  else if (key === 'pending') styles = 'bg-amber-50 text-amber-700';
  else if (key === 'in review' || key === 'inreview') styles = 'bg-blue-50 text-blue-700';
  else if (key === 'rejected' || key === 'failed') styles = 'bg-red-50 text-red-700';

  return (
    <span className={`inline-flex px-2.5 py-1 rounded-full text-xs font-medium ${styles}`}>
      {status || '—'}
    </span>
  );
};

const AccountStatusBadge = ({ status }) => {
  const key = (status || '').toLowerCase();
  let styles = 'bg-emerald-50 text-emerald-700';
  if (key === 'archived') styles = 'bg-gray-200 text-gray-700';
  else if (key === 'inactive') styles = 'bg-amber-50 text-amber-700';

  return (
    <span className={`inline-flex px-2.5 py-1 rounded-full text-xs font-medium ${styles}`}>
      {status || 'Active'}
    </span>
  );
};

const Clients = () => {
  const { isDarkMode } = useTheme();
  const [searchParams] = useSearchParams();
  const initialSearch = (searchParams.get('search') || searchParams.get('clientCode') || '').trim();
  const [clients, setClients] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState(initialSearch);
  const [appliedSearchTerm, setAppliedSearchTerm] = useState(initialSearch);
  const [loginIdSearch, setLoginIdSearch] = useState('');
  const [appliedLoginId, setAppliedLoginId] = useState('');
  const [kycFilter, setKycFilter] = useState('All');
  const [appliedKycFilter, setAppliedKycFilter] = useState('All');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState('10');
  const [expandedRows, setExpandedRows] = useState(new Set());
  const [isExporting, setIsExporting] = useState(false);
  const [pagination, setPagination] = useState({
    totalRecords: 0,
    totalPages: 1,
    pageNumber: 1,
    pageSize: 10,
    totalClients: 0,
    activeClients: 0,
    totalLots: 0,
    totalProfit: 0,
  });

  const apiPageSize = Number(pageSize) || 10;
  const apiSearchTerm =
    appliedKycFilter !== 'All' ? appliedKycFilter : appliedSearchTerm;
  const listFetchKey = `clients|${currentPage}|${apiPageSize}|${apiSearchTerm}|${appliedLoginId}`;

  const fetchClients = useCallback(async ({ cancelled }) => {
    setIsLoading(true);
    try {
      const response = await apiService.getClientsList({
        pageNumber: currentPage,
        pageSize: apiPageSize,
        searchTerm: apiSearchTerm,
        loginId: appliedLoginId,
        type: '',
        sortBy: '',
        sortOrder: '',
      });
      if (cancelled()) return;

      const list = extractClientsList(response).map(mapClient);
      const meta = extractClientsListPagination(response, apiPageSize);
      setClients(list);
      setPagination(meta);
    } catch {
      if (!cancelled()) {
        setClients([]);
        setPagination({
          totalRecords: 0,
          totalPages: 1,
          pageNumber: currentPage,
          pageSize: apiPageSize,
          totalClients: 0,
          activeClients: 0,
          totalLots: 0,
          totalProfit: 0,
        });
      }
    } finally {
      if (!cancelled()) setIsLoading(false);
    }
  }, [currentPage, apiPageSize, apiSearchTerm, appliedLoginId]);

  useFetchOnceByKey(listFetchKey, fetchClients, true);

  useEffect(() => {
    setCurrentPage(1);
  }, [pageSize]);

  const toggleRow = (id) => {
    setExpandedRows((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleApplyFilters = () => {
    setAppliedSearchTerm(searchTerm.trim());
    setAppliedLoginId(loginIdSearch.trim());
    setAppliedKycFilter(kycFilter);
    setCurrentPage(1);
  };

  const handleResetFilters = () => {
    setSearchTerm('');
    setAppliedSearchTerm('');
    setLoginIdSearch('');
    setAppliedLoginId('');
    setKycFilter('All');
    setAppliedKycFilter('All');
    setCurrentPage(1);
  };

  const handleExportExcel = async () => {
    setIsExporting(true);
    try {
      const exportRows = await fetchAllClientsForExport(apiSearchTerm, appliedLoginId);
      if (!exportRows.length) return;

      // Real .xlsx built in the browser (nothing is stored on the server); exceljs loads only when exporting.
      const ExcelJS = (await import('exceljs')).default;
      const workbook = new ExcelJS.Workbook();
      const sheet = workbook.addWorksheet('Clients');

      sheet.columns = [
        { header: 'S.No', key: 'sno', width: 7 },
        { header: 'Name', key: 'name', width: 26 },
        { header: 'User Code', key: 'code', width: 16 },
        { header: 'Trading Accounts', key: 'accounts', width: 34 },
        { header: 'Registration Date', key: 'date', width: 18 },
        { header: 'KYC Status', key: 'kyc', width: 14 },
        { header: 'Deposit', key: 'deposit', width: 10 },
        { header: 'First Deposit', key: 'first', width: 15, style: { numFmt: '#,##0.00' } },
        { header: 'Total Deposit (Approved)', key: 'totalDeposit', width: 24, style: { numFmt: '#,##0.00' } },
        { header: 'Total Withdrawal (Approved)', key: 'totalWithdrawal', width: 27, style: { numFmt: '#,##0.00' } },
        { header: 'Total Lots', key: 'lots', width: 12, style: { numFmt: '#,##0.00' } },
        { header: 'Total Commission', key: 'commission', width: 18, style: { numFmt: '#,##0.00' } },
      ];

      exportRows.forEach((client, idx) => {
        sheet.addRow({
          sno: idx + 1,
          name: client.fullName || '—',
          code: formatClientCode(client.userCode),
          accounts:
            client.tradingAccounts
              .map((a) => (a.isArchived ? `${a.loginId} (Archived)` : a.loginId))
              .join(', ') || '0',
          date: formatDate(client.registrationDate),
          kyc: client.kycStatus,
          deposit: client.isDeposit ? 'Yes' : 'No',
          first: client.firstDeposit || 0,
          totalDeposit: client.totalDeposit,
          totalWithdrawal: client.totalWithdrawal,
          lots: client.totalLots,
          commission: client.totalCommission,
        });
      });

      const header = sheet.getRow(1);
      header.font = { bold: true, color: { argb: 'FFFFFFFF' } };
      header.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF8B0000' } };
      header.alignment = { vertical: 'middle' };
      header.height = 22;
      sheet.views = [{ state: 'frozen', ySplit: 1 }];
      sheet.autoFilter = { from: 'A1', to: 'L1' };

      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `clients-${new Date().toISOString().split('T')[0]}.xlsx`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1500);
    } catch {
      /* ignore */
    } finally {
      setIsExporting(false);
    }
  };

  const totalPages = Math.max(1, Number(pagination.totalPages) || 1);
  const totalRecords = pagination.totalRecords || clients.length;
  const showingFrom = totalRecords === 0 ? 0 : (currentPage - 1) * apiPageSize + 1;
  const showingTo = Math.min(currentPage * apiPageSize, totalRecords);
  const pendingCount = clients.filter((c) => c.kycStatus.toLowerCase() === 'pending').length;

  const card = isDarkMode ? 'bg-gray-900 border-gray-800' : 'bg-white border-gray-100';
  const text = isDarkMode ? 'text-white' : 'text-gray-900';
  const muted = isDarkMode ? 'text-gray-400' : 'text-gray-500';
  const inputCls = `w-full rounded-xl border px-3 py-2.5 text-sm outline-none transition focus:border-red-800 focus:ring-2 focus:ring-red-800/15 ${
    isDarkMode ? 'bg-gray-800 border-gray-700 text-white' : 'bg-white border-gray-200 text-gray-900'
  }`;

  return (
    <div className={`min-h-full ${isDarkMode ? 'bg-black' : 'bg-[#F4F5F7]'}`}>
      <div className="max-w-[1280px] mx-auto px-4 sm:px-6 py-5 sm:py-6">
        <div className="mb-6">
          <h1 className={`text-2xl font-bold ${text}`}>Clients</h1>
          <p className={`text-sm mt-1 ${muted}`}>
            <Link to="/" className="hover:text-red-800">Dashboard</Link>
            <span className="mx-1.5">›</span>
            <span>Clients</span>
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
          <div className={`rounded-2xl border p-4 shadow-sm ${card}`}>
            <p className={`text-xs ${muted}`}>Total Clients</p>
            <p className={`text-xl font-bold mt-1 ${text}`}>
              {(pagination.totalClients || totalRecords).toLocaleString('en-US')}
            </p>
          </div>
          <div className={`rounded-2xl border p-4 shadow-sm ${card}`}>
            <p className={`text-xs ${muted}`}>Pending KYC (page)</p>
            <p className={`text-xl font-bold mt-1 ${text}`}>{pendingCount.toLocaleString('en-US')}</p>
          </div>
        </div>

        <div className={`rounded-2xl border p-4 sm:p-5 shadow-sm mb-6 ${card}`}>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-[1fr_1fr_200px_auto] gap-3 items-end">
            <div>
              <label className={`block text-xs font-medium mb-1.5 ${muted}`}>Search</label>
              <div className="relative">
                <Search className={`absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 ${muted}`} />
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Name or user code, e.g. A-UC-00275"
                  className={`${inputCls} pl-9`}
                />
              </div>
            </div>
            <div>
              <label className={`block text-xs font-medium mb-1.5 ${muted}`}>Login ID</label>
              <div className="relative">
                <Search className={`absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 ${muted}`} />
                <input
                  type="text"
                  inputMode="numeric"
                  value={loginIdSearch}
                  onChange={(e) => setLoginIdSearch(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') handleApplyFilters();
                  }}
                  placeholder="Trading account login…"
                  className={`${inputCls} pl-9`}
                />
              </div>
            </div>
            <div>
              <label className={`block text-xs font-medium mb-1.5 ${muted}`}>KYC Status</label>
              <select
                value={kycFilter}
                onChange={(e) => setKycFilter(e.target.value)}
                className={inputCls}
              >
                {KYC_TABS.map((tab) => (
                  <option key={tab} value={tab}>{tab}</option>
                ))}
              </select>
            </div>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={handleApplyFilters}
                className="px-4 py-2.5 rounded-xl text-sm font-semibold text-white"
                style={{ backgroundColor: THEME }}
              >
                Apply
              </button>
              <button
                type="button"
                onClick={handleResetFilters}
                className={`px-4 py-2.5 rounded-xl text-sm font-medium border ${
                  isDarkMode
                    ? 'border-gray-700 text-gray-300 hover:bg-gray-800'
                    : 'border-gray-200 text-gray-700 hover:bg-gray-50'
                }`}
              >
                Reset
              </button>
              <button
                type="button"
                onClick={handleExportExcel}
                disabled={isExporting}
                className={`px-4 py-2.5 rounded-xl text-sm font-medium border transition disabled:opacity-60 ${
                  isDarkMode
                    ? 'border-red-800 text-red-300 hover:bg-red-950'
                    : 'border-red-200 text-red-800 hover:bg-red-50'
                }`}
              >
                {isExporting ? 'Exporting…' : 'Export Excel'}
              </button>
            </div>
          </div>
        </div>

        <div className={`rounded-2xl border shadow-sm overflow-hidden ${card}`}>
          <div className={`px-5 py-4 border-b flex items-center gap-2 ${isDarkMode ? 'border-gray-800' : 'border-gray-100'}`}>
            <Users className="w-4 h-4 text-red-800" />
            <h2 className={`text-sm font-semibold ${text}`}>Client List</h2>
            <span className={`text-xs ml-auto ${muted}`}>{totalRecords.toLocaleString('en-US')} records</span>
          </div>

          {isLoading ? (
            <div className="flex justify-center py-16">
              <NewtonsCradleLoader />
            </div>
          ) : clients.length === 0 ? (
            <div className={`text-center py-16 px-4 ${muted}`}>
              <p className="text-sm">
                {appliedSearchTerm || appliedLoginId || appliedKycFilter !== 'All'
                  ? 'No clients match your filters.'
                  : 'No clients found.'}
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full min-w-[1280px]">
                <thead>
                  <tr className={`text-left text-xs font-medium border-b ${isDarkMode ? 'border-gray-800 text-gray-400' : 'border-gray-100 text-gray-500'}`}>
                    <th className="px-4 py-3 w-10" />
                    <th className="px-3 py-3">Name</th>
                    <th className="px-3 py-3">User Code</th>
                    <th className="px-3 py-3">Trading Accounts</th>
                    <th className="px-3 py-3">Registration Date</th>
                    <th className="px-3 py-3">KYC Status</th>
                    <th className="px-3 py-3">Deposit</th>
                    <th className="px-3 py-3">First Deposit</th>
                    <th className="px-3 py-3">Total Deposit (Approved)</th>
                    <th className="px-3 py-3">Total Withdrawal (Approved)</th>
                    <th className="px-3 py-3">Total Lots</th>
                    <th className="px-3 py-3">Total Commission</th>
                    <th className="px-3 py-3">Action</th>
                  </tr>
                </thead>
                <tbody className={`divide-y ${isDarkMode ? 'divide-gray-800' : 'divide-gray-50'}`}>
                  {clients.map((client) => {
                    const expanded = expandedRows.has(client.id);
                    const canExpand = client.tradingAccounts.length > 0;
                    return (
                      <React.Fragment key={client.id}>
                        <tr className={isDarkMode ? 'hover:bg-gray-800/50' : 'hover:bg-gray-50/80'}>
                          <td className="px-4 py-3.5">
                            {canExpand ? (
                              <button
                                type="button"
                                onClick={() => toggleRow(client.id)}
                                className={`inline-flex items-center justify-center w-7 h-7 rounded-lg border transition ${
                                  isDarkMode
                                    ? 'border-gray-700 text-gray-400 hover:bg-gray-800'
                                    : 'border-gray-200 text-gray-500 hover:bg-gray-50'
                                }`}
                                aria-label={expanded ? 'Collapse accounts' : 'Expand accounts'}
                              >
                                {expanded ? (
                                  <ChevronDown className="w-3.5 h-3.5" />
                                ) : (
                                  <ChevronRight className="w-3.5 h-3.5" />
                                )}
                              </button>
                            ) : (
                              <span className="inline-block w-7" />
                            )}
                          </td>
                          <td className={`px-3 py-3.5 text-sm font-semibold ${text}`}>{client.fullName || '—'}</td>
                          <td className={`px-3 py-3.5 text-sm font-medium ${text}`}>{formatClientCode(client.userCode)}</td>
                          <td className={`px-3 py-3.5 text-sm ${text}`}>
                            {client.tradingAccountsCount.toLocaleString('en-US')}
                          </td>
                          <td className={`px-3 py-3.5 text-sm ${text}`}>{formatDate(client.registrationDate)}</td>
                          <td className="px-3 py-3.5"><KycBadge status={client.kycStatus} /></td>
                          <td className="px-3 py-3.5">
                            <span
                              className={`inline-flex px-2.5 py-1 rounded-full text-xs font-medium ${
                                client.isDeposit
                                  ? 'bg-emerald-50 text-emerald-700'
                                  : 'bg-gray-100 text-gray-600'
                              }`}
                            >
                              {client.isDeposit ? 'Yes' : 'No'}
                            </span>
                          </td>
                          <td className={`px-3 py-3.5 text-sm font-semibold ${text}`}>
                            {new Intl.NumberFormat('en-US', {
                              style: 'currency',
                              currency: 'USD',
                              minimumFractionDigits: 2,
                            }).format(client.firstDeposit || 0)}
                          </td>
                          <td className={`px-3 py-3.5 text-sm font-semibold ${text}`}>{formatMoney(client.totalDeposit)}</td>
                          <td className={`px-3 py-3.5 text-sm font-semibold ${text}`}>{formatMoney(client.totalWithdrawal)}</td>
                          <td className={`px-3 py-3.5 text-sm font-semibold ${text}`}>{formatLots(client.totalLots)}</td>
                          <td className={`px-3 py-3.5 text-sm font-semibold ${text}`}>{formatMoney(client.totalCommission)}</td>
                          <td className="px-3 py-3.5">
                            <Link
                              to={`/earnings/list?clientCode=${encodeURIComponent(
                                client.userCode && client.userCode !== '—' ? client.userCode : ''
                              )}`}
                              className="text-sm font-semibold hover:underline"
                              style={{ color: THEME }}
                            >
                              View Commission
                            </Link>
                          </td>
                        </tr>
                        {expanded && canExpand && (
                          <tr className={isDarkMode ? 'bg-gray-800/30' : 'bg-gray-50/80'}>
                            <td colSpan={13} className="px-5 py-4">
                              <div className={`rounded-xl border p-4 ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
                                <p className={`text-xs font-semibold mb-3 ${text}`}>Trading Accounts</p>
                                <div className="overflow-x-auto">
                                  <table className="w-full min-w-[360px]">
                                    <thead>
                                      <tr className={`text-left text-[11px] font-medium border-b ${isDarkMode ? 'border-gray-700 text-gray-400' : 'border-gray-200 text-gray-500'}`}>
                                        <th className="px-3 py-2">#</th>
                                        <th className="px-3 py-2">Account Login</th>
                                        <th className="px-3 py-2">Status</th>
                                      </tr>
                                    </thead>
                                    <tbody className={`divide-y ${isDarkMode ? 'divide-gray-700' : 'divide-gray-100'}`}>
                                      {client.tradingAccounts.map((account, idx) => (
                                        <tr key={`${client.id}-${account.loginId}-${idx}`}>
                                          <td className={`px-3 py-2.5 text-xs ${muted}`}>{idx + 1}</td>
                                          <td className={`px-3 py-2.5 text-sm font-medium ${text}`}>
                                            {account.loginId}
                                          </td>
                                          <td className="px-3 py-2.5">
                                            <AccountStatusBadge status={account.status} />
                                          </td>
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                </div>
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {!isLoading && totalRecords > 0 && (
            <div className={`px-5 py-4 border-t flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 ${isDarkMode ? 'border-gray-800' : 'border-gray-100'}`}>
              <div className="flex flex-wrap items-center gap-4">
                <div className="flex items-center gap-2">
                  <span className={`text-xs ${muted}`}>Show</span>
                  <select
                    value={pageSize}
                    onChange={(e) => setPageSize(e.target.value)}
                    className={`rounded-lg border px-2 py-1.5 text-xs outline-none focus:border-red-800 focus:ring-2 focus:ring-red-800/15 min-w-[3.5rem] ${
                      isDarkMode ? 'bg-gray-800 border-gray-700 text-white' : 'bg-white border-gray-200 text-gray-900'
                    }`}
                  >
                    {PAGE_SIZE_OPTIONS.map((o) => (
                      <option key={o.value} value={o.value}>{o.label}</option>
                    ))}
                  </select>
                  <span className={`text-xs ${muted}`}>entries</span>
                </div>
                <p className={`text-xs ${muted}`}>
                  Showing {showingFrom} to {showingTo} of {totalRecords} entries
                </p>
              </div>
              {totalPages > 1 && (
                <div className="flex items-center gap-1">
                  <button
                    type="button"
                    disabled={currentPage <= 1}
                    onClick={() => setCurrentPage((p) => p - 1)}
                    className={`px-3 py-1.5 rounded-lg border text-xs font-medium disabled:opacity-40 disabled:cursor-not-allowed ${
                      isDarkMode ? 'border-gray-700 text-gray-300 hover:bg-gray-800' : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    Prev
                  </button>
                  {Array.from({ length: totalPages }, (_, i) => i + 1)
                    .filter((page) => page === 1 || page === totalPages || Math.abs(page - currentPage) <= 1)
                    .map((page, i, arr) => (
                      <React.Fragment key={page}>
                        {i > 0 && arr[i - 1] !== page - 1 && (
                          <span className={`px-1 text-xs ${muted}`}>…</span>
                        )}
                        <button
                          type="button"
                          onClick={() => setCurrentPage(page)}
                          className={`min-w-[2rem] px-2 py-1.5 rounded-lg border text-xs font-medium ${
                            currentPage === page
                              ? 'text-white border-transparent'
                              : isDarkMode
                                ? 'border-gray-700 text-gray-300 hover:bg-gray-800'
                                : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                          }`}
                          style={currentPage === page ? { backgroundColor: THEME } : {}}
                        >
                          {page}
                        </button>
                      </React.Fragment>
                    ))}
                  <button
                    type="button"
                    disabled={currentPage >= totalPages}
                    onClick={() => setCurrentPage((p) => p + 1)}
                    className={`px-3 py-1.5 rounded-lg border text-xs font-medium disabled:opacity-40 disabled:cursor-not-allowed ${
                      isDarkMode ? 'border-gray-700 text-gray-300 hover:bg-gray-800' : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    Next
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Clients;
